import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, icons } from "../../Styles";
import { UNIT, SecondaryPopover } from "../";
import { LocaleFarmDate } from "../../Definitions";
import { useQuery } from "@apollo/client";
import { TerminalInfo } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getTerminalsCall";
import { LC_TERMINAL_ROWS, LCTerminalRow, LCTerminalRowsResponse } from "../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lcTerminalRows";

interface MotorSpecs_Props{
  farm: string;
  terminals: TerminalInfo[];
}

// We should probably make these components independent of its parents
export default function MotorSpecs({ terminals, farm }: MotorSpecs_Props) {
  const {data, loading} = useQuery<LCTerminalRowsResponse>(LC_TERMINAL_ROWS, {variables: {domain: farm, terminalIds: terminals?.map(t => t.terminal_id)}, fetchPolicy: "no-cache", pollInterval: 10000})

  if (!terminals || terminals.length === 0) return null;

  const terminalOrder = [50, 80, 81, 4, 82, 52];
 // some fetch dispatch of terminals by multiple ids.
  const sortedTerminals = terminals
  .slice(0, 3)
  .sort((a, b) => {
    const indexA = terminalOrder.indexOf(a.fk_terminal_types ?? 50);
    const indexB = terminalOrder.indexOf(b.fk_terminal_types ?? 50);
    return indexA - indexB;
  });

  return (
    <div style={{ display: "flex", gap: "10px", width: "fit-content"}}>
      {sortedTerminals?.slice(0, 3).map((terminal, index) => {
        return (
          // Same with this components
          <MotorComponent terminal={terminal} liveTerminalInfo={data?.controller.lcTerminalRows.find(l => l.terminalId === terminal.terminal_id)} />)
      })}
    </div>
  )
}

// #region | Single Motor Component
const MotorComponent = memo(({ terminal, liveTerminalInfo }: { terminal: TerminalInfo, liveTerminalInfo?: LCTerminalRow }) => {
  const { t } = useTranslation();
  const formatLabel = (label: string) => label.replace(/([a-z])([A-Z])/g, "$1 $2");
  
  // #region | Terminal Types
  const TerminalType: Record<number, {label: string, src: string}> = {
    50: { label: t("motorSpecs.terminalTypes.defaultMotor", {defaultValue: "Def. Motor"}), src: icons.motorLF },
    80: { label: t("motorSpecs.terminalTypes.locationFeeder", {defaultValue: "Feeder"}), src: icons.feederLF },
    81: { label: t("motorSpecs.terminalTypes.locationFeeder", {defaultValue: "Feeder"}), src: icons.feederLF },
    4: { label: t("motorSpecs.terminalTypes.activator", {defaultValue: "Activator"}), src: icons.activatorLF },
    82: { label: t("motorSpecs.terminalTypes.activator", {defaultValue: "Activator"}), src: icons.activatorLF },
    52: { label: t("motorSpecs.terminalTypes.water", {defaultValue: "Water"}), src: icons.water },
    5: {label: "Gate sensor", src: icons.animals},
    54: {label: "Gate motor", src: icons.animals}, // Both the gate and the feeder are currently this type
    200: {label: "reader", src: icons.animals},
  };
  // This needs to be rethought
  // #endregion
  
  const noLiveData = !liveTerminalInfo
  const skipTime = Number(terminal?.skip_time ?? 0) * 1000
  const isSkipped = skipTime > Date.now()
  const skipTimeToString = new LocaleFarmDate(isSkipped ? skipTime : "").toLocaleFarmString()
  const skipCnt = liveTerminalInfo !== undefined ? liveTerminalInfo.skipCnt : ""

  const fallbackTexts = {
    skipCnt: t("motorSpecs.general.noSkip", { defaultValue: "No Skip" }),
    skipTime: t("motorSpecs.general.noSkip", { defaultValue: "No Skip" }),
    powerConsumption: t("motorSpecs.general.noPowerConsumption", { defaultValue: "No Power Consumption" }),
    calibrationValue: t("motorSpecs.general.noCalibrationValue", { defaultValue: "No Calibration Value" }),
    rotationTime: t("motorSpecs.general.noRotationTime", { defaultValue: "No Rotation Time" }),
    statusMode: t("motorSpecs.general.noStatusMode", { defaultValue: "No Status Mode" }),
  };

  let validatedCalibrationValue = undefined;
  if (terminal.val1_type === 2) {
    validatedCalibrationValue = terminal.val1_min;
  }
  if (terminal.val2_type === 2) {
    validatedCalibrationValue = terminal.val2_min;
  }

  let validatedPowerConsumption = undefined;
  if (terminal.val1_type === 1) {
    validatedPowerConsumption = terminal.val1_max;
  }
  if (terminal.val2_type === 1) {
    validatedPowerConsumption = terminal.val2_max;
  }

  const specs: Record<string, { value: string | number | undefined, unit: string }> = {
    skipCnt: { value: skipCnt === 0 ? "" : skipCnt, unit: "" },
    skipTime: { value: skipTimeToString, unit: ""},
    powerConsumption: { value: validatedPowerConsumption, unit: "m/A" },
    ...(terminal.fk_terminal_types !== 4 && {
      calibrationValue: { value: validatedCalibrationValue, unit: t("units.g", {defaultValue: "g"}) },
    }),
    rotationTime: { value: terminal.time_act, unit: t("units.sec", {defaultValue: "sec"}) },
    statusMode: { value: terminal.programmed, unit: "" },
  };
  
  let status: { value: string, unit: string } = { value: "", unit: "" };
  if (terminal.programmed) {
    var arr = terminal.programmed.toString(2).split("").reverse();
    
    if (arr.length >= 1 && arr[0] === "1") {
      status = { value: "disabled", unit: "" };
    }
    else if (arr.length >= 5 && arr[4] === "1") {
      status = { value: "calibration", unit: "" };
    }
    else if (arr.length >= 3 && arr[2] === "1") {
      status = { value: "feedingOnFeedingTimes", unit: "" };
    }
    else if (arr.length >= 2 && arr[1] === "1") {
      status = { value: "midnightTrigger", unit: "" }; // TODO better text
    }
    else {
      status = { value: "motorPassive", unit: "" };
    }
    // 0 1 0 0 0
  }

  const cntActivated = liveTerminalInfo?.activations ?? 0
  const cntPending = liveTerminalInfo?.periodecomp ?? 0

  const content = (() => {
    switch (terminal.fk_terminal_types) {
      case 4:
        return (
          <MotorSpecContent
            isSkipped={isSkipped}
            noLiveData={noLiveData}
            skipTimeToString={skipTimeToString}
            pending={cntPending.toString()}
            fed={cntActivated.toString()}
            pendingUnit={<UNIT content={t("motorSpecs.general.pending", {defaultValue: "pending"})} color={colors.greyPrimary} />}
            fedUnit={<UNIT content={t("motorSpecs.general.activated", {defaultValue: "activated"})} color={colors.greyPrimary} />}
          />
        );
      case 50: case 80: case 81:
        return (
          <MotorSpecContent
            isSkipped={isSkipped}
            noLiveData={noLiveData}
            skipTimeToString={skipTimeToString}
            pending={((cntPending * (validatedCalibrationValue ?? 0)) / 1000).toFixed(2)}
            fed={((cntActivated * (validatedCalibrationValue ?? 0)) / 1000).toFixed(2)}
            pendingUnit={<UNIT content={t("motorSpecs.general.pending", {defaultValue: "pending"})} color={colors.greyPrimary} />}
            fedUnit={<UNIT content={t("units.kg", {defaultValue: "kg"})} color={colors.greyPrimary} glyph={glyphs.Kg({ size: "15px", fillColor: colors.greyPrimary })} />}
          />
        );
      case 52:
        return (
          <MotorSpecContent
            isSkipped={isSkipped}
            noLiveData={noLiveData}
            skipTimeToString={skipTimeToString}
            pending={((cntPending * (validatedCalibrationValue ?? 0)) / 1000).toFixed(2)}
            fed={((cntActivated * (validatedCalibrationValue ?? 0)) / 1000).toFixed(2)}
            pendingUnit={<UNIT content="pending" color={colors.greyPrimary} />}
            fedUnit={<UNIT content={"fed"} color={colors.greyPrimary} glyph={glyphs.Kg({ size: "15px", fillColor: colors.greyPrimary })} />}
          />
        );
      default: return <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyPrimary, fontStyle: "italic" }}>{`${t("units.noData", {defaultValue: "No data"})}`}</p>;
    }
  })();
  

  return (
    <SecondaryPopover
      popoverHeader={TerminalType[terminal.fk_terminal_types ?? 50].label}
      popoverContent={
        <div style={{}}>
          {Object.entries(specs).map(([label, { value, unit }]) => {
            const noValue = value === null || value === ""
              
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr", gap: "10px" }}>
                <div style={{ gridArea: "1/1/2/2", ...fonts.paragraph, ...fonts.fontWeight2, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "right" }}>{t(`motorSpecs.general.${label}`, {defaultValue: formatLabel(label) })}</div>
                <div style={{ gridArea: "1/2/2/3", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, fontStyle: noValue ? "italic" : "none", opacity: noValue ? "0.5" : "1" }}>
                  { noValue
                    ? fallbackTexts[label as keyof typeof fallbackTexts]
                    : `${label === "statusMode" ? t(`motorSpecs.terminalProgrammed.${status.value}`, {defaultValue: status.value}) : value} ${label === "statusMode" ? status.unit : unit}`
                  }
                </div>
              </div>

            )
          })}
        </div>
      }
      placement="bottom"
      content={<>
        <div style={{ display: "grid", gridTemplateRows: "40px 2px 14px 1fr 18px", gridTemplateColumns: "1fr", gap: "5px", width: "110px", backgroundColor: colors.greyLightSecondary, borderRadius: "10px", height: "200px", padding: "5px", boxSizing: "border-box" }}>
          <div style={{ gridArea: "1/1/2/2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={TerminalType[terminal.fk_terminal_types ?? 50].src} alt="duttelut" style={{width: "40px"}}/>
          </div>

          <div style={{ gridArea: "2/1/3/2", borderTop: "2px solid " + colors.greyTertiary, margin: "0px 5px" }} />

          <div style={{ gridArea: "3/1/4/2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <p style={{ ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>{TerminalType[terminal.fk_terminal_types ?? 50].label}</p>
          </div>
          
          <div style={{ gridArea: "4/1/5/2", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "5px" }}>
            {content}
          </div>

          <div style={{ gridArea: "5/1/6/2", height: "18px", display: "flex", justifyContent: "center" }}>
            <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center", width: "90px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{t(`motorSpecs.terminalProgrammed.${status.value}`, {defaultValue: status.value})}</p>
          </div>

        </div>
      </>}
    /> 
  )
})
// #endregion

// #region | Motor Inner Content
const MotorSpecContent = memo(({ isSkipped, noLiveData, skipTimeToString, pending, fed, pendingUnit, fedUnit }: {
  isSkipped: boolean,
  noLiveData: boolean,
  skipTimeToString: string,
  pending: string, 
  pendingUnit: JSX.Element,
  fed: string,
  fedUnit: JSX.Element,
}) => {
  const { t } = useTranslation();

  if (isSkipped) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{`${t("motorSpecs.general.skippedUntil", {defaultValue: "Skipped until"})} :`}</p>
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center" }}>{skipTimeToString}</p>
    </div>
  )
  
  if (noLiveData) return (
    <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyPrimary, fontStyle: "italic" }}>{`${t("motorSpecs.general.noLiveData", {defaultValue: "No live data"})}`}</p>
  )

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <glyphs.Arrow fillColor={colors.graphBlueTwo} rotate={90} size="15px" />
          <p style={{ color: colors.graphBlueTwo, ...fonts.fontSize1, ...fonts.header }}> {pending} </p>
        </div>
        <div style={{ height: "17px", display: "flex", alignItems: "center" }}>{pendingUnit}</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          {<glyphs.Arrow fillColor={colors.graphBlueOne} rotate={-90} size="15px" />}
          <p style={{ color: colors.graphBlueOne, ...fonts.fontSize1, ...fonts.header }}> {fed} </p>
        </div>
        <div style={{ height: "17px", display: "flex", alignItems: "center" }}>{fedUnit}</div>
      </div>
    </>
  )
})
// #endregion